import { useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  FileSignature,
  KeyRound,
  ListOrdered,
  LogOut,
  UserCog,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Item,
  ItemContent,
  ItemTitle,
  ItemActions,
  ItemGroup,
  ItemMedia,
  ItemSeparator,
} from "@/components/ui/item";
import {
  useAuthProvider,
  useCanAccess,
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useLogout,
  useNotify,
  useTranslate,
} from "ra-core";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import type { CrmDataProvider } from "../providers/types";
import { ChangePasswordDialog } from "../sales/ChangePasswordDialog";

const ChangePasswordButton = () => {
  const translate = useTranslate();

  return (
    <ChangePasswordDialog
      trigger={
        <Button variant="outline" className="w-full text-base h-auto">
          <KeyRound className="size-5 mr-3" />
          {translate("crm.profile.password.change")}
        </Button>
      }
    />
  );
};

export const SettingsPageMobile = () => {
  const translate = useTranslate();
  const authProvider = useAuthProvider();
  const logout = useLogout();

  if (!authProvider) return null;

  return (
    <>
      <MobileHeader>
        <h1 className="text-xl font-semibold text-brand-navy">
          {translate("crm.settings.title")}
        </h1>
      </MobileHeader>
      <MobileContent>
        <div className="flex flex-col min-h-[calc(100dvh-3.5rem-4.5rem)]">
          <div className="space-y-6">
            <ProfileSection />
            <AdminSection />
          </div>

          <div className="mt-auto pt-6 space-y-3 mb-4">
            <ChangePasswordButton />
            <Button
              variant="destructive"
              className="w-full text-base h-auto"
              onClick={() => logout()}
            >
              <LogOut className="size-5 mr-3" />
              {translate("ra.auth.logout")}
            </Button>
          </div>
        </div>
      </MobileContent>
    </>
  );
};

SettingsPageMobile.path = "/settings";

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1 mb-1.5">
    {children}
  </p>
);

/**
 * Admin-only links of the settings hub. Each entry is guarded by its own
 * permission (the same ones the desktop sidebar uses); when none is granted
 * the whole section disappears.
 */
const AdminSection = () => {
  const translate = useTranslate();
  const { canAccess: canManageUsers } = useCanAccess({
    resource: "sales",
    action: "create",
  });
  const { canAccess: canManageContractTypes } = useCanAccess({
    resource: "contract_types",
    action: "create",
  });
  const { canAccess: canEditConfiguration } = useCanAccess({
    resource: "configuration",
    action: "edit",
  });

  const links = [
    canManageUsers
      ? {
          to: "/sales",
          Icon: UserCog,
          label: translate("resources.sales.name", { smart_count: 2 }),
        }
      : null,
    canManageContractTypes
      ? {
          to: "/contract_types",
          Icon: FileSignature,
          label: translate("resources.contract_types.name", {
            smart_count: 2,
          }),
        }
      : null,
    canEditConfiguration
      ? {
          to: "/settings/fasen",
          Icon: ListOrdered,
          label: translate("crm.settings.sponsorings.stages"),
        }
      : null,
  ].filter((link) => link != null);

  if (links.length === 0) return null;

  return (
    <div>
      <SectionLabel>{translate("crm.navigation.admin")}</SectionLabel>
      <ItemGroup className="rounded-lg border overflow-hidden">
        {links.map((link, index) => (
          <Fragment key={link.to}>
            {index > 0 ? <ItemSeparator /> : null}
            <Item size="sm" asChild className="rounded-none">
              <Link to={link.to}>
                <ItemMedia>
                  <link.Icon className="size-5 text-brand-blue" />
                </ItemMedia>
                <ItemContent>
                  <ItemTitle className="text-base font-normal">
                    {link.label}
                  </ItemTitle>
                </ItemContent>
                <ItemActions>
                  <ChevronRight className="size-4 text-muted-foreground" />
                </ItemActions>
              </Link>
            </Item>
          </Fragment>
        ))}
      </ItemGroup>
    </div>
  );
};

const ProfileSection = () => {
  const { identity, refetch: refetchIdentity } = useGetIdentity();
  const { data, refetch: refetchUser } = useGetOne("sales", {
    id: identity?.id,
  });
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();

  const saveField = useCallback(
    async (field: string, value: string) => {
      if (!identity || !data) return;
      const current = data[field as keyof typeof data];
      if (value === current) return;

      const queryKey = [
        "sales",
        "getOne",
        { id: String(identity.id), meta: undefined },
      ];
      const previousData = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: any) =>
        old ? { ...old, [field]: value } : old,
      );

      try {
        await dataProvider.salesUpdate(identity.id, { [field]: value });
        refetchIdentity();
        refetchUser();
        notify("crm.profile.updated");
      } catch {
        queryClient.setQueryData(queryKey, previousData);
        notify("crm.profile.update_error", { type: "error" });
      }
    },
    [
      identity,
      data,
      dataProvider,
      refetchIdentity,
      refetchUser,
      notify,
      queryClient,
    ],
  );

  if (!identity || !data) return null;

  return (
    <div>
      <SectionLabel>{translate("crm.profile.title")}</SectionLabel>
      <ItemGroup className="rounded-lg border overflow-hidden">
        <InlineEditRow
          label={translate("resources.sales.fields.first_name")}
          value={data.first_name ?? ""}
          onSave={(v) => saveField("first_name", v)}
        />

        <ItemSeparator />

        <InlineEditRow
          label={translate("resources.sales.fields.last_name")}
          value={data.last_name ?? ""}
          onSave={(v) => saveField("last_name", v)}
        />

        <ItemSeparator />

        <Item size="sm">
          <ItemContent>
            <ItemTitle className="font-normal text-muted-foreground">
              {translate("resources.sales.fields.email")}
            </ItemTitle>
          </ItemContent>
          <ItemActions>
            <span className="text-base">{data.email ?? ""}</span>
          </ItemActions>
        </Item>
      </ItemGroup>
    </div>
  );
};

const InlineEditRow = ({
  label,
  value,
  onSave,
}: {
  label: string;
  value: string;
  onSave: (value: string) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const handleSave = useCallback(() => {
    setIsEditing(false);
    const trimmed = editValue.trim();
    if (trimmed !== value) {
      onSave(trimmed);
    }
  }, [editValue, value, onSave]);

  const handleCancel = useCallback(() => {
    setEditValue(value);
    setIsEditing(false);
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        inputRef.current?.blur();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    },
    [handleCancel],
  );

  if (isEditing) {
    return (
      <Item size="sm">
        <ItemContent>
          <ItemTitle className="font-normal text-muted-foreground">
            {label}
          </ItemTitle>
        </ItemContent>
        <ItemActions>
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className="bg-transparent text-right !text-base outline-none w-48"
          />
        </ItemActions>
      </Item>
    );
  }

  return (
    <Item
      size="sm"
      className="cursor-pointer"
      onClick={() => setIsEditing(true)}
    >
      <ItemContent>
        <ItemTitle className="font-normal text-muted-foreground">
          {label}
        </ItemTitle>
      </ItemContent>
      <ItemActions>
        <span className="text-base">{value}</span>
      </ItemActions>
    </Item>
  );
};
